import { useContext, useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { InfluencerDataContext } from '../../../../contexts/InfluencerDataContext';
import { influencerApi } from '../../../../services/api/influencer';
import { shouldShowDiamondCelebration } from './diamondCelebrationUtils';

interface UseDiamondCelebrationOptions {
  /** When false the modal stays closed (e.g. while editing a profile section). */
  enabled?: boolean;
}

/**
 * One-time Diamond Tier celebration. Mirrors useWalletPayoutIntro: the seen flag lives on
 * the creator's record rather than in browser storage, so dismissing it on one device
 * settles it everywhere.
 *
 * No read call needed — InfluencerDataContext subscribes to the creator's document, so an
 * admin marking them arrives here on its own.
 */
export function useDiamondCelebration(options: UseDiamondCelebrationOptions = {}) {
  const { enabled = true } = options;
  const { influencerData } = useContext(InfluencerDataContext);
  const [dismissedLocally, setDismissedLocally] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const shouldShow =
    enabled && shouldShowDiamondCelebration(influencerData) && !dismissedLocally;

  useEffect(() => {
    if (shouldShow) {
      onOpen();
    } else {
      onClose();
    }
  }, [shouldShow, onOpen, onClose]);

  // Re-marking a creator who already dismissed this re-arms the flag server-side, and the
  // context is subscribed to their doc — but dismissedLocally would keep the modal hidden for
  // the life of this mount. Depend on the flag itself, not on influencerData: dismissing must
  // not re-run this before the write lands, or it would undo the dismissal.
  useEffect(() => {
    if (influencerData?.showDiamondTierIntro === true) {
      setDismissedLocally(false);
    }
  }, [influencerData?.id, influencerData?.showDiamondTierIntro]);

  const dismiss = async (): Promise<void> => {
    setDismissedLocally(true);
    onClose();
    const influencerId = influencerData?.id;
    if (!influencerId) {
      return;
    }

    try {
      await influencerApi.updateProfile({ showDiamondTierIntro: false }, influencerId);
    } catch (err) {
      console.error('Failed to dismiss Diamond Tier celebration:', err);
    }
  };

  // Guarded by `enabled` itself, not just the caller's gating on it: a staff switched-account
  // view passes enabled=false specifically so dismissing never writes the viewed creator's
  // flag, and a badge click must not be able to route around that.
  const openDiamondModal = () => {
    if (enabled) {
      onOpen();
    }
  };

  // isCelebrationPending is the same predicate the modal opens on, but available on the first
  // render rather than a render later like isOpen. Callers sequencing another modal behind this
  // one want this: it also releases as soon as the creator dismisses, even if persisting failed.
  return {
    isCelebrationOpen: isOpen,
    isCelebrationPending: shouldShow,
    dismissCelebration: dismiss,
    openDiamondModal,
  };
}
